import React, { useContext, useMemo } from 'react';

import { Avatar } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChevronRightIcon } from '../../../../../../../assets/icons/v3/chevron-right.svg';
import { ReactComponent as LogoutIcon } from '../../../../../../../assets/icons/v3/logout.svg';
import { ReactComponent as SettingsIcon } from '../../../../../../../assets/icons/v3/settings.svg';
import { INTEGRATIONS_ROUTE } from '../../../../../../constants/auth';
import { PROFILE_MENU_ITEMS } from '../../../../../../constants/constants';
import { CAN_SEE_INTEGRATIONS_FG } from '../../../../../../constants/statsig';
import { CLICK_ACTION, PROFILE_SECTION } from '../../../../../../constants/tagManager';
import { SessionContext } from '../../../../../../context/auth/authContext';
import { IProfileMenuItem } from '../../../../../../interfaces/global';
import tagManagerService from '../../../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../../translations/commons';
import { getInitialsLetters, redirectToPage } from '../../../../../../utils/utils';

import './ProfileMenu.scss';

const ProfileMenu = () => {
  const { user, sessionLogout } = useContext(SessionContext);
  const username = useMemo(() => user.name || user.email.split('@')[0], [user.name, user.email]);
  const navigate = useNavigate();
  const { client } = useStatsigClient();

  const { t } = useTranslation();

  const handleClick = (item: IProfileMenuItem): void => {
    tagManagerService.pushEvent(CLICK_ACTION, PROFILE_SECTION, item.gtmSection);

    if (item.route === 'logout') {
      sessionLogout();
    } else {
      redirectToPage(navigate, item.route);
    }
  };

  const filteredMenuItems = useMemo(() => {
    return PROFILE_MENU_ITEMS.filter((item) => {
      if (item.route === INTEGRATIONS_ROUTE) {
        return client?.checkGate(CAN_SEE_INTEGRATIONS_FG);
      }
      return true;
    });
  }, [client]);

  const renderMenuItem = (item: IProfileMenuItem, isLogout: boolean, index: number): JSX.Element => (
    <button
      className={`profile-menu-item ${isLogout ? 'logout' : ''} `}
      key={`${item.name}-${index}`}
      onClick={() => handleClick(item)}
      aria-label={`Menu item ${t(item.name)}`}
    >
      {isLogout && <LogoutIcon className="logout-icon" />}
      <div className="item-text">{t(item.name)}</div>
      {!isLogout && <ChevronRightIcon className="item-icon" aria-hidden="true" />}
    </button>
  );

  return (
    <div className="profile-menu-container">
      <div className="profile-menu-header">
        <Avatar
          className={`user-avatar ${user.image ? 'avatar-image' : 'avatar-initials'}`}
          alt={username}
          style={{ backgroundImage: `url("${user.image}")` }}
        >
          {!user.image && getInitialsLetters(username, ' ')}
        </Avatar>
        <div className="header-name">{username}</div>
      </div>
      <div className="profile-menu-settings">
        <SettingsIcon className="settings-icon" />
        <div className="settings-name">{t(TRANSLATIONS_GLOBAL.header.profileMenu.settings)}</div>
      </div>
      <div className="profile-menu-items">
        {filteredMenuItems.map((item: IProfileMenuItem, index: number) => {
          const isLogoutItem = item.route === 'logout';
          return renderMenuItem(item, isLogoutItem, index);
        })}
      </div>
    </div>
  );
};

export default ProfileMenu;
